import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { inr, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, Landmark, HandCoins, TrendingUp, TrendingDown, AlertTriangle,
  ShieldCheck, Check, X, BarChart3, BookOpen, FileText, ChevronRight, Store,
} from "lucide-react";

const typeLabel = { transaction: "Cash entry", invoice: "Invoice", voucher: "Voucher / Receipt" };

function iso(d) { return d.toISOString().slice(0, 10); }
function presets() {
  const now = new Date();
  const t = iso(now);
  return {
    today: { start: t, end: t },
    week: { start: iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6)), end: t },
    month: { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: t },
  };
}

function MoneyCard({ icon: Icon, label, value, tone = "default", sub }) {
  const tones = {
    default: "border-border bg-card text-foreground",
    accent: "border-accent/20 bg-accent/5 text-accent",
    danger: "border-destructive/20 bg-destructive/5 text-destructive",
    positive: "border-[hsl(var(--positive))]/20 bg-[hsl(var(--positive))]/5 text-[hsl(var(--positive))]",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]} animate-fade-up`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <Icon size={16} />
      </div>
      <div className="num text-xl font-bold mt-2 text-foreground">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function OwnerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [range, setRange] = useState(() => ({ ...presets().today, key: "today" }));
  const [busy, setBusy] = useState("");
  const [storeUn, setStoreUn] = useState(null);
  const [shorts, setShorts] = useState([]);

  useEffect(() => {
    const s = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
    api.get("/reports/closing-differences", { params: { start: s } })
      .then((r) => setShorts((r.data.items || []).filter((d) => d.cash_diff < -0.009 || d.bank_diff < -0.009).reverse()))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    api.get("/store-sync/unlinked-summary").then((r) => setStoreUn(r.data)).catch((e) => console.error(e));
  }, []);

  const load = useCallback(async (start = range.start, end = range.end) => {
    try {
      const [d, r, a] = await Promise.all([
        api.get("/dashboard", { params: { start, end } }),
        api.get("/reminders"),
        api.get("/deletion-requests"),
      ]);
      setData(d.data); setReminders(r.data); setReqs(a.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail ?? e?.message)); }
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const setPreset = (key) => { const p = presets()[key]; setRange({ ...p, key }); };

  const act = async (r, action) => {
    const verb = action === "approve" ? (r.action === "edit" ? "Approve this edit" : "Approve and delete") : "Reject";
    if (!window.confirm(`${verb} ${r.label}?`)) return;
    setBusy(r.id);
    try {
      await api.post(`/deletion-requests/${r.id}/${action}`);
      toast.success(action === "approve" ? "Approved" : "Rejected");
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail ?? e?.message)); }
    setBusy("");
  };

  const b = data?.balances || {};
  const pd = data?.period || b || {};
  const rangeLabel = range.key === "today" ? "Today" : range.key === "week" ? "This week" : range.key === "month" ? "This month" : "Custom range";

  return (
    <Layout reminders={reminders.length}>
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <div className="mb-5 animate-fade-up">
          <h1 className="font-display text-3xl font-semibold tracking-tight">Owner View</h1>
          <p className="text-sm text-muted-foreground mt-0.5 num">{user?.company_name} · {iso(new Date())}</p>
        </div>

        {/* Range toggle */}
        <div className="flex gap-2 mb-3">
          {[["today", "Today"], ["week", "Week"], ["month", "Month"], ["custom", "Custom"]].map(([k, l]) => (
            <button key={k} data-testid={`owner-range-${k}`} onClick={() => k === "custom" ? setRange((r) => ({ ...r, key: "custom" })) : setPreset(k)}
              className={`flex-1 h-10 rounded-md text-sm font-medium border transition-colors ${range.key === k ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
        {range.key === "custom" && (
          <div data-testid="owner-custom-range" className="flex items-end gap-2 mb-4 animate-fade-up">
            <div className="flex-1"><div className="text-[11px] text-muted-foreground mb-0.5">From</div>
              <input data-testid="owner-custom-start" type="date" value={range.start}
                onChange={(e) => setRange((r) => ({ ...r, start: e.target.value, key: "custom" }))}
                className="w-full h-10 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40" /></div>
            <div className="flex-1"><div className="text-[11px] text-muted-foreground mb-0.5">To</div>
              <input data-testid="owner-custom-end" type="date" value={range.end}
                onChange={(e) => setRange((r) => ({ ...r, end: e.target.value, key: "custom" }))}
                className="w-full h-10 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40" /></div>
          </div>
        )}

        {/* Credit reminder */}
        {reminders.length > 0 && (
          <div data-testid="owner-reminder" className="mb-4 rounded-xl border border-destructive/20 bg-destructive/5 p-3.5 flex items-start gap-2.5 animate-fade-up">
            <AlertTriangle className="text-destructive mt-0.5 shrink-0" size={18} />
            <div className="text-sm"><span className="font-semibold text-destructive">{reminders.length} party credit limit{reminders.length > 1 ? "s" : ""} exceeded.</span>{" "}
              <span className="text-muted-foreground">{reminders.slice(0, 3).map((r) => r.name).join(", ")}{reminders.length > 3 ? "…" : ""}</span></div>
          </div>
        )}

        {shorts.length > 0 && (
          <button data-testid="owner-shortage-alert" onClick={() => navigate("/reports")}
            className="w-full text-left mb-4 rounded-xl border border-destructive/30 bg-destructive/5 p-3.5 flex items-start gap-2.5 animate-fade-up hover:bg-destructive/10 transition-colors">
            <AlertTriangle className="text-destructive mt-0.5 shrink-0" size={18} />
            <div className="text-sm flex-1 min-w-0">
              <span data-testid="owner-shortage-count" className="font-semibold text-destructive">{shorts.length} day{shorts.length > 1 ? "s" : ""} locked with a shortage</span>
              <span className="text-muted-foreground"> · last 30 days</span>
              <div className="mt-1 space-y-0.5">
                {shorts.slice(0, 3).map((d) => (
                  <div key={d.date} data-testid={`owner-shortage-${d.date}`} className="text-xs num truncate">
                    {d.date} · {d.cash_diff < -0.009 && <span className="text-destructive">cash −{inr(-d.cash_diff)} </span>}
                    {d.bank_diff < -0.009 && <span className="text-destructive">bank −{inr(-d.bank_diff)} </span>}
                    <span className="text-muted-foreground">· {d.reason || "no reason"} · {d.by}</span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">Total short: <span className="num font-semibold text-destructive">{inr(shorts.reduce((s, d) => s + Math.max(-d.cash_diff, 0) + Math.max(-d.bank_diff, 0), 0))}</span> · Tap for Reports → Closing Differences</div>
            </div>
            <ChevronRight className="text-muted-foreground mt-0.5 shrink-0" size={16} />
          </button>
        )}

        {storeUn?.count > 0 && (
          <button data-testid="owner-store-unlinked-alert" onClick={() => navigate("/store-invoices")}
            className="w-full text-left mb-4 rounded-xl border border-accent/30 bg-accent/5 p-3.5 flex items-start gap-2.5 animate-fade-up hover:bg-accent/10 transition-colors">
            <Store className="text-accent mt-0.5 shrink-0" size={18} />
            <div className="text-sm flex-1">
              <span data-testid="owner-store-unlinked-count" className="font-semibold text-foreground">{storeUn.count} AutoGarage bill{storeUn.count > 1 ? "s" : ""} not linked</span>{" "}
              <span className="text-muted-foreground num">· {inr(storeUn.amount)} · oldest {storeUn.oldest_date}</span>
              <div className="text-[11px] text-muted-foreground mt-0.5">Tap to link them to a Khata invoice</div>
            </div>
            <ChevronRight className="text-muted-foreground mt-0.5 shrink-0" size={16} />
          </button>
        )}

        {/* Snapshot */}
        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <MoneyCard icon={Wallet} label="Cash in Hand" value={inr(b.cash_balance)} tone="accent" />
              <MoneyCard icon={Landmark} label="Bank Balance" value={inr(b.bank_balance)} />
              <MoneyCard icon={HandCoins} label="Credit Outstanding" value={inr(data.total_credit_outstanding)} tone={data.over_limit_count ? "danger" : "default"} sub={`${data.credit_party_count} parties`} />
              <MoneyCard icon={FileText} label="Invoices Raised" value={data.invoice_count} sub={rangeLabel} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="rounded-xl border border-[hsl(var(--positive))]/20 bg-[hsl(var(--positive))]/5 p-4 animate-fade-up">
                <div className="flex items-center gap-1.5 text-[hsl(var(--positive))] text-xs font-semibold"><TrendingUp size={14} /> Money In · {rangeLabel}</div>
                <div className="num text-xl font-bold mt-1.5">{inr((pd.cash_in || 0) + (pd.bank_in || 0))}</div>
                <div className="text-[11px] text-muted-foreground mt-1 num">Cash {inr(pd.cash_in)} · Bank {inr(pd.bank_in)}</div>
              </div>
              <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 animate-fade-up">
                <div className="flex items-center gap-1.5 text-destructive text-xs font-semibold"><TrendingDown size={14} /> Money Out · {rangeLabel}</div>
                <div className="num text-xl font-bold mt-1.5">{inr((pd.cash_out || 0) + (pd.bank_out || 0))}</div>
                <div className="text-[11px] text-muted-foreground mt-1 num">Cash {inr(pd.cash_out)} · Bank {inr(pd.bank_out)}</div>
              </div>
            </div>
          </>
        )}

        {/* Approvals */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><ShieldCheck size={18} className="text-accent" /> Approvals</h2>
          {reqs.length > 0 && <span data-testid="owner-approval-count" className="text-xs font-bold bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full num">{reqs.length}</span>}
        </div>
        {reqs.length === 0 ? (
          <div data-testid="owner-approvals-empty" className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground mb-6">
            <ShieldCheck className="mx-auto mb-2 opacity-40" size={26} /> No pending requests. All clear.
          </div>
        ) : (
          <div className="space-y-2.5 mb-6">
            {reqs.map((r) => (
              <div key={r.id} data-testid={`owner-approval-${r.id}`} className="rounded-xl border border-border bg-card p-3.5 animate-fade-up">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{typeLabel[r.entity_type] || r.entity_type}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${r.action === "edit" ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>{r.action === "edit" ? "Edit" : "Delete"}</span>
                  <span className="num font-bold ml-auto">{inr(r.amount)}</span>
                </div>
                <div className="font-medium text-sm mt-1.5 truncate">{r.label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 num">{r.entry_date ? `${r.entry_date} · ` : ""}by {r.requested_by_name}{r.reason ? ` · "${r.reason}"` : ""}</div>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`owner-approve-${r.id}`} disabled={busy === r.id} onClick={() => act(r, "approve")}
                    className="flex-1 h-10 rounded-md bg-destructive text-destructive-foreground text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-destructive/90 transition-colors disabled:opacity-50">
                    <Check size={15} /> {r.action === "edit" ? "Approve edit" : "Approve"}
                  </button>
                  <button data-testid={`owner-reject-${r.id}`} disabled={busy === r.id} onClick={() => act(r, "reject")}
                    className="flex-1 h-10 rounded-md border border-input bg-card text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-muted transition-colors disabled:opacity-50">
                    <X size={15} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick links */}
        <h2 className="font-display text-lg font-semibold mb-3">Reports</h2>
        <div className="space-y-2.5">
          {[
            { to: "/reports", icon: BarChart3, label: "Full Reports", sub: "Cash book, invoices, credit, flow" },
            { to: "/cashbook", icon: BookOpen, label: "Cash Book", sub: "Daily entries & balances" },
            { to: "/invoices", icon: FileText, label: "Invoices", sub: "Raised, paid & outstanding" },
          ].map((l) => {
            const Icon = l.icon;
            return (
              <button key={l.to} data-testid={`owner-link-${l.to.slice(1)}`} onClick={() => navigate(l.to)}
                className="w-full rounded-xl border border-border bg-card p-4 flex items-center gap-3 text-left hover:border-accent/40 transition-colors">
                <span className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0"><Icon size={18} /></span>
                <div className="min-w-0 flex-1"><div className="font-medium text-sm">{l.label}</div><div className="text-xs text-muted-foreground truncate">{l.sub}</div></div>
                <ChevronRight size={18} className="text-muted-foreground shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
